import type { GatewayBrowserClient } from "../gateway.ts";
import type { AgentsListResult, SessionsListResult } from "../types.ts";
import type { WorkspaceTreeNode } from "../types.ts";
import type { ConfigState } from "./config.ts";
import {
  collectAgentYamlPaths,
  markRegisteredCandidates,
  parseAgentYamlDefinition,
  type WorkspaceAgentCandidate,
} from "../chat/discover-workspace-agents.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";
import {
  addAgentToConfigForm,
  findAgentListIndex,
  getAgentsConfigList,
  updateAgentFieldsInConfigForm,
} from "./agents-config.ts";
import { patchAgentModel, patchAgentSkills } from "./agents-list-config-patch.ts";
import { formatChatWorkspaceError, isStaleWorkspaceSessionKeyError } from "./chat-workspace.ts";
import { saveConfig } from "./config.ts";
import { loadSessions } from "./sessions.ts";

export type AgentsImportState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentsList: AgentsListResult | null;
  sessionsResult: SessionsListResult | null;
  sessionsLoading: boolean;
  agentsImportSessionKey: string;
  agentsImportLoading: boolean;
  agentsImportError: string | null;
  agentsImportCandidates: WorkspaceAgentCandidate[];
  agentsImportSelectedIds: string[];
  agentsImportWorkspacePath: string | null;
  agentsImportBusy: boolean;
};

export async function loadSessionsForAgentsImport(state: AgentsImportState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.sessionsLoading = true;
  try {
    await loadSessions(state as unknown as Parameters<typeof loadSessions>[0], {
      limit: 120,
      includeLastMessage: true,
      includeDerivedTitles: true,
    });
  } finally {
    state.sessionsLoading = false;
  }
}

export async function discoverAgentsFromSession(state: AgentsImportState, sessionKey: string) {
  const trimmed = sessionKey.trim();
  state.agentsImportSessionKey = trimmed;
  state.agentsImportCandidates = [];
  state.agentsImportSelectedIds = [];
  state.agentsImportError = null;
  state.agentsImportWorkspacePath = null;

  if (!trimmed || !state.client || !state.connected) {
    return;
  }

  state.agentsImportLoading = true;
  try {
    const defaultAgentId = state.agentsList?.defaultId ?? "main";
    const agentId = resolveAgentIdForSession(trimmed, defaultAgentId);
    let res: { workspace?: string; tree?: WorkspaceTreeNode[] };
    try {
      res = await state.client.request("agents.workspace.list", {
        agentId,
        sessionKey: trimmed,
      });
    } catch (err) {
      if (!isStaleWorkspaceSessionKeyError(err)) {
        throw err;
      }
      res = await state.client.request("agents.workspace.list", { agentId });
    }
    const tree = Array.isArray(res?.tree) ? res.tree : [];
    state.agentsImportWorkspacePath = res?.workspace ?? null;
    const paths = collectAgentYamlPaths(tree);
    const parsed: import("../chat/discover-workspace-agents.ts").ParsedWorkspaceAgent[] = [];
    const seen = new Set<string>();
    for (const path of paths) {
      let content = "";
      try {
        const read = await state.client.request<{ content?: string; truncated?: boolean }>(
          "agents.workspace.read",
          { agentId, sessionKey: trimmed, path },
        );
        content = read?.content ?? "";
        if (read?.truncated) {
          continue;
        }
      } catch {
        continue;
      }
      const def = parseAgentYamlDefinition(content, path);
      if (!def || seen.has(def.id)) {
        continue;
      }
      seen.add(def.id);
      parsed.push(def);
    }
    const registered = new Set(
      (state.agentsList?.agents ?? []).map((a) => a.id.trim()).filter(Boolean),
    );
    state.agentsImportCandidates = markRegisteredCandidates(parsed, registered);
    const selectable = state.agentsImportCandidates
      .filter((c) => !c.alreadyRegistered)
      .map((c) => c.id);
    state.agentsImportSelectedIds = selectable;
  } catch (err) {
    state.agentsImportError = formatChatWorkspaceError(err);
    state.agentsImportCandidates = [];
  } finally {
    state.agentsImportLoading = false;
  }
}

export function toggleAgentsImportSelection(state: AgentsImportState, agentId: string) {
  const id = agentId.trim();
  if (!id) {
    return;
  }
  const set = new Set(state.agentsImportSelectedIds);
  if (set.has(id)) {
    set.delete(id);
  } else {
    set.add(id);
  }
  state.agentsImportSelectedIds = [...set];
}

export async function importSelectedAgentsFromChat(
  state: AgentsImportState & ConfigState,
): Promise<{ ok: boolean; imported: string[]; error?: string }> {
  const selected = state.agentsImportSelectedIds.filter((id) =>
    state.agentsImportCandidates.some((c) => c.id === id && !c.alreadyRegistered),
  );
  if (selected.length === 0) {
    return { ok: false, imported: [], error: "Select at least one agent to import." };
  }
  if (!state.configForm && !state.configSnapshot?.config) {
    return { ok: false, imported: [], error: "Config not loaded." };
  }

  state.agentsImportBusy = true;
  state.agentsImportError = null;
  const imported: string[] = [];
  try {
    for (const id of selected) {
      const candidate = state.agentsImportCandidates.find((c) => c.id === id);
      if (!candidate) {
        continue;
      }
      if (findAgentListIndex(state.configForm ?? state.configSnapshot?.config ?? null, id) >= 0) {
        continue;
      }
      const added = addAgentToConfigForm(state, {
        id: candidate.id,
        name: candidate.name,
      });
      if (!added.ok) {
        state.agentsImportError = added.error;
        break;
      }
      if (candidate.model) {
        patchAgentModel(state.configForm ?? {}, id, candidate.model);
        state.configFormDirty = true;
      }
      if (candidate.skills?.length) {
        patchAgentSkills(state.configForm ?? {}, id, candidate.skills);
        state.configFormDirty = true;
      }
      if (candidate.name) {
        updateAgentFieldsInConfigForm(state, id, {
          name: candidate.name,
          identityName: candidate.name,
        });
      }
      imported.push(id);
    }
    if (imported.length === 0) {
      return {
        ok: false,
        imported: [],
        error: state.agentsImportError ?? "Nothing imported.",
      };
    }
    await saveConfig(state);
    if (state.lastError) {
      return { ok: false, imported, error: state.lastError };
    }
    const registered = new Set(
      getAgentsConfigList(state.configForm ?? state.configSnapshot?.config ?? null).map((e) =>
        String(e.id ?? "").trim(),
      ),
    );
    state.agentsImportCandidates = markRegisteredCandidates(
      state.agentsImportCandidates.map(({ alreadyRegistered: _, ...c }) => c),
      registered,
    );
    state.agentsImportSelectedIds = [];
    return { ok: true, imported };
  } catch (err) {
    return { ok: false, imported, error: String(err) };
  } finally {
    state.agentsImportBusy = false;
  }
}
